import React from 'react';

import { isEmpty } from 'es-toolkit/compat';
import { matchPath, useLocation, useNavigate } from 'react-router';

import { appRoutes } from '@clearverify/constants';

import { EmptyInbox } from 'src/components/inbox';
import { useInboxItemById, useInboxItemParamId } from 'src/hooks/inboxItem';
import {
  useTestVerificationRequest,
  useTestVerificationRequestsRoute,
} from 'src/hooks/testVerificationRequests';
import { useCurrentUserPermissions } from 'src/hooks/user';
import { OpenDataDisputeModal } from 'src/modals/dataDispute';
import {
  ViewApiVerificationRequestReportModal,
  ViewVerificationRequestReportModal,
} from 'src/modals/verifier';
import { InboxFilterProvider } from 'src/providers/InboxFilterProvider';
import { SearchProvider } from 'src/providers/SearchProvider';
import { VerificationRequestProcessingProvider } from 'src/providers/VerificationRequestProcessingProvider';

import { InboxPageConsumer } from './InboxPageConsumer';
import { InboxPageCVStaff } from './InboxPageCVStaff';
import { InboxPageEmployer } from './InboxPageEmployer';
import { InboxPagePayrollProvider } from './InboxPagePayrollProvider';
import { InboxPagePEOStaff } from './InboxPagePEOStaff';
import { InboxPageVerifierStaff } from './InboxPageVerifierStaff';

export const InboxPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    hasPlatformRoles,
    hasVerifierRoles,
    hasEmployerRoles,
    hasPeoRoles,
    isEmployee,
    isPeoPayrollProviderUser,
  } = useCurrentUserPermissions();

  const inboxItemId = useInboxItemParamId();
  const { inboxItem, loading: loadingInboxItem } = useInboxItemById(inboxItemId);

  const { isTestRequestsRoute } = useTestVerificationRequestsRoute();
  const { testVerificationRequest, loading: loadingTestVerificationRequest } =
    useTestVerificationRequest(inboxItemId, { skip: !isTestRequestsRoute });

  // Navigate to empty inbox page if inboxItem wasn't found
  React.useEffect(() => {
    if (
      !matchPath({ path: appRoutes.inbox.index }, location.pathname) &&
      !matchPath({ path: appRoutes.inbox.inboxTestRequests }, location.pathname) &&
      !loadingInboxItem &&
      !loadingTestVerificationRequest &&
      isEmpty(inboxItem) &&
      isEmpty(testVerificationRequest)
    ) {
      navigate(appRoutes.inbox.index);
    }
  }, [
    inboxItem,
    navigate,
    location.pathname,
    loadingInboxItem,
    loadingTestVerificationRequest,
    testVerificationRequest,
  ]);

  const inboxPage = React.useMemo(() => {
    if (hasPlatformRoles) {
      return <InboxPageCVStaff />;
    }

    if (isPeoPayrollProviderUser) {
      return <InboxPagePayrollProvider />;
    }

    if (hasPeoRoles) {
      return <InboxPagePEOStaff />;
    }

    if (hasEmployerRoles) {
      return <InboxPageEmployer />;
    }

    if (hasVerifierRoles) {
      return <InboxPageVerifierStaff />;
    }

    if (isEmployee) {
      return <InboxPageConsumer />;
    }

    return null;
  }, [
    hasPlatformRoles,
    hasVerifierRoles,
    hasEmployerRoles,
    hasPeoRoles,
    isEmployee,
    isPeoPayrollProviderUser,
  ]);

  if (!inboxPage) {
    return <EmptyInbox />;
  }

  return (
    <InboxFilterProvider>
      <SearchProvider>
        <VerificationRequestProcessingProvider>
          {inboxPage}
          <ViewVerificationRequestReportModal />
          <ViewApiVerificationRequestReportModal />
          <OpenDataDisputeModal />
        </VerificationRequestProcessingProvider>
      </SearchProvider>
    </InboxFilterProvider>
  );
};
